/**
 * CvDiffViewer — field-by-field CV diff with accept/reject per change.
 *
 * Props:
 * - before: the original CVData
 * - after:  the AI-generated CVData
 * - onApply(merged): called when user clicks "Appliquer"
 * - onCancel: called if user clicks "Annuler"
 *
 * Change types:
 * - summary (personalInfo.summary / title)
 * - experience items (matched by index, description diff)
 * - skills (added/removed)
 */

import { useState } from 'react';
import { CheckCircle2, XCircle, CheckSquare, Square } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CVData } from '../../types';
import { useI18n } from '../../contexts/I18nContext';

// ---- Types ----

type ChangeType =
  | { kind: 'summary'; before: string; after: string; field: 'summary' | 'title' }
  | { kind: 'experience'; index: number; position: string; company: string; before: string; after: string }
  | { kind: 'skills_added'; values: string[] }
  | { kind: 'skills_removed'; values: string[] };

interface Change {
  id: string;
  change: ChangeType;
}

// ---- Diff computation ----

function computeDiff(before: CVData, after: CVData): Change[] {
  const changes: Change[] = [];

  // Summary
  if (before.personalInfo.summary !== after.personalInfo.summary && after.personalInfo.summary) {
    changes.push({ id: 'summary', change: { kind: 'summary', before: before.personalInfo.summary, after: after.personalInfo.summary, field: 'summary' } });
  }

  // Title
  if (before.personalInfo.title !== after.personalInfo.title && after.personalInfo.title) {
    changes.push({ id: 'title', change: { kind: 'summary', before: before.personalInfo.title, after: after.personalInfo.title, field: 'title' } });
  }

  // Experiences (match by index)
  const maxExp = Math.max(before.experience.length, after.experience.length);
  for (let i = 0; i < maxExp; i++) {
    const b = before.experience[i];
    const a = after.experience[i];
    if (!a) continue; // AI removed experience — skip (don't force-remove)
    if (!b) continue; // AI added experience — skip for now (complex merge)
    if (b.description !== a.description && a.description) {
      changes.push({
        id: `exp-${i}`,
        change: { kind: 'experience', index: i, position: a.position || b.position, company: a.company || b.company, before: b.description, after: a.description },
      });
    }
  }

  // Skills diff
  const beforeSet = new Set(before.skills);
  const afterSet = new Set(after.skills);
  const added = after.skills.filter(s => !beforeSet.has(s));
  const removed = before.skills.filter(s => !afterSet.has(s));
  if (added.length > 0) changes.push({ id: 'skills-added', change: { kind: 'skills_added', values: added } });
  if (removed.length > 0) changes.push({ id: 'skills-removed', change: { kind: 'skills_removed', values: removed } });

  return changes;
}

function applyChanges(before: CVData, after: CVData, accepted: Set<string>, changes: Change[]): CVData {
  const merged: CVData = {
    personalInfo: { ...before.personalInfo },
    experience: before.experience.map(e => ({ ...e })),
    education: before.education.map(e => ({ ...e })),
    skills: [...before.skills],
    languages: [...before.languages],
    hobbies: [...before.hobbies],
  };

  for (const { id, change } of changes) {
    if (!accepted.has(id)) continue;
    switch (change.kind) {
      case 'summary':
        merged.personalInfo = { ...merged.personalInfo, [change.field]: change.after };
        break;
      case 'experience':
        if (merged.experience[change.index]) {
          merged.experience[change.index] = { ...merged.experience[change.index]!, description: change.after };
        }
        break;
      case 'skills_added':
        merged.skills = [...merged.skills, ...change.values];
        break;
      case 'skills_removed':
        merged.skills = merged.skills.filter(s => !change.values.includes(s));
        break;
    }
  }

  // Also apply after's experience descriptions for non-diffed fields (company name, position if changed)
  for (let i = 0; i < Math.min(merged.experience.length, after.experience.length); i++) {
    const expChange = changes.find(c => c.id === `exp-${i}`);
    if (!expChange || !accepted.has(expChange.id)) {
      // Even if description not accepted, update position/company if changed
      const a = after.experience[i]!;
      if (a.position && a.position !== merged.experience[i]?.position) {
        // Don't auto-apply position changes without user consent
      }
    }
  }

  return merged;
}

// ---- Main component ----

interface CvDiffViewerProps {
  before: CVData;
  after: CVData;
  onApply: (merged: CVData) => void;
  onCancel: () => void;
}

export function CvDiffViewer({ before, after, onApply, onCancel }: CvDiffViewerProps) {
  const { t } = useI18n();
  const changes = computeDiff(before, after);
  const [accepted, setAccepted] = useState<Set<string>>(() => new Set(changes.map(c => c.id)));

  if (changes.length === 0) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-white/60 text-center py-4">{t('diff.noChanges')}</p>
        <button type="button" onClick={onCancel} className="text-sm text-white/50 hover:text-white/70 transition-colors">{t('diff.close')}</button>
      </div>
    );
  }

  const toggle = (id: string) => {
    setAccepted(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (accepted.size === changes.length) {
      setAccepted(new Set());
    } else {
      setAccepted(new Set(changes.map(c => c.id)));
    }
  };

  const allAccepted = accepted.size === changes.length;
  const acceptedCount = accepted.size;

  const handleApply = () => {
    const merged = applyChanges(before, after, accepted, changes);
    onApply(merged);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-white/60">
          {t('diff.modificationsProposed')
            .replace('{n}', String(changes.length))
            .replace(/{s}/g, changes.length > 1 ? 's' : '')}
        </p>
        <button
          type="button"
          onClick={toggleAll}
          className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/70 transition-colors"
        >
          {allAccepted ? <CheckSquare className="size-3.5" /> : <Square className="size-3.5" />}
          {allAccepted ? t('diff.deselectAll') : t('diff.selectAll')}
        </button>
      </div>

      {/* Changes list */}
      <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
        {changes.map(({ id, change }) => (
          <ChangeCard
            key={id}
            id={id}
            change={change}
            accepted={accepted.has(id)}
            onToggle={() => toggle(id)}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-white/10">
        <button type="button" onClick={onCancel} className="text-sm text-white/40 hover:text-white/60 transition-colors">
          {t('diff.cancel')}
        </button>
        <button
          type="button"
          onClick={handleApply}
          disabled={acceptedCount === 0}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-600/20"
        >
          <CheckCircle2 className="size-4" />
          {t('diff.apply')
            .replace('{n}', String(acceptedCount))
            .replace(/{s}/g, acceptedCount > 1 ? 's' : '')}
        </button>
      </div>
    </div>
  );
}

// ---- Change card ----

function ChangeCard({ id: _id, change, accepted, onToggle }: {
  id: string; change: ChangeType; accepted: boolean; onToggle: () => void;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border p-3 space-y-2 cursor-pointer transition-all select-none',
        accepted ? 'border-blue-500/30 bg-blue-500/5' : 'border-white/[0.07] bg-white/[0.01] opacity-50',
      )}
      onClick={onToggle}
    >
      <div className="flex items-start justify-between gap-2">
        <ChangeLabel change={change} />
        <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }} className="shrink-0 mt-0.5">
          {accepted
            ? <CheckCircle2 className="size-4 text-blue-400" />
            : <XCircle className="size-4 text-white/30" />
          }
        </button>
      </div>
      <ChangeDiff change={change} />
    </div>
  );
}

function ChangeLabel({ change }: { change: ChangeType }) {
  const { t } = useI18n();
  switch (change.kind) {
    case 'summary':
      return <span className="text-xs font-semibold text-white/60">{change.field === 'title' ? t('diff.fieldTitle') : t('diff.fieldSummary')}</span>;
    case 'experience':
      return <span className="text-xs font-semibold text-white/60">{t('diff.fieldExperience')} — {change.position} · {change.company}</span>;
    case 'skills_added':
      return <span className="text-xs font-semibold text-emerald-400">{t('diff.skillsAdded').replace('{n}', String(change.values.length))}</span>;
    case 'skills_removed':
      return <span className="text-xs font-semibold text-red-400">{t('diff.skillsRemoved').replace('{n}', String(change.values.length))}</span>;
  }
}

function ChangeDiff({ change }: { change: ChangeType }) {
  const { t } = useI18n();
  switch (change.kind) {
    case 'summary':
    case 'experience':
      return (
        <div className="space-y-1.5 text-xs">
          <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-2 text-red-300/80 line-clamp-3">{change.before || t('diff.empty')}</div>
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-2 text-emerald-300/80 line-clamp-3">{change.after}</div>
        </div>
      );
    case 'skills_added':
    case 'skills_removed':
      return (
        <div className="flex flex-wrap gap-1">
          {change.values.map(v => (
            <span key={v} className={cn(
              'px-2 py-0.5 rounded-full text-[11px] font-medium',
              change.kind === 'skills_added' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/20' : 'bg-red-500/15 text-red-300 border border-red-500/20',
            )}>
              {v}
            </span>
          ))}
        </div>
      );
  }
}
